"use client";

import {
  Mic,
  MicOff,
  Phone,
  Monitor,
  User,
  Headset,
  LogOut,
} from "lucide-react";
import { useCallStore } from "@/store/call-store";
import { clock, cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ui/theme-toggle";

interface TopbarProps {
  startListening: () => void;
  stopListening: () => void;
  isListening: boolean;
}

export function Topbar({ startListening, stopListening, isListening }: TopbarProps) {
  const elapsed = useCallStore((s) => s.elapsed);
  const toggleRunning = useCallStore((s) => s.toggleRunning);
  const running = useCallStore((s) => s.running);
  
  const callMode = useCallStore((s) => s.callMode);
  const setCallMode = useCallStore((s) => s.setCallMode);
  const currentSpeaker = useCallStore((s) => s.currentSpeaker);
  const setCurrentSpeaker = useCallStore((s) => s.setCurrentSpeaker);

  const handleMicClick = () => {
    if (isListening) {
      stopListening();
      toggleRunning();
    } else {
      startListening();
      if (!running) toggleRunning();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("varta_groq_key");
    window.location.reload();
  };

  return (
    <div className="flex flex-col z-20 shrink-0 border-b border-line bg-bg-elevated/80 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.02)]">
      
      {/* Authentic macOS Titlebar */}
      <div className="flex h-7 items-center justify-between px-3 w-full bg-[#1e1e1e]/90 dark:bg-black/90">
        <div className="flex items-center gap-2">
          <div className="size-3 rounded-full bg-[#ff5f56]" />
          <div className="size-3 rounded-full bg-[#ffbd2e]" />
          <div className="size-3 rounded-full bg-[#27c93f]" />
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 text-[11px] font-semibold tracking-wide text-[#888888]">
          <img src="/logo.png" alt="" className="size-3 rounded-sm opacity-80" />
          <span>varta — bash — 80x24</span>
        </div>
        <div className="w-[52px]" /> {/* Spacer for centering */}
      </div>

      {/* App Toolbar */}
      <header className="flex h-12 items-center justify-between gap-3 px-4 md:px-6">
        
        {/* Modes */}
        <div className="flex items-center gap-1 bg-card-muted p-1 rounded-lg border border-line">
          <button
            onClick={() => setCallMode("meet")}
            className={cn(
              "flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-all",
              callMode === "meet" ? "bg-white dark:bg-card text-primary shadow-sm ring-1 ring-border-strong" : "text-text-tertiary hover:text-text"
            )}
          >
            <Monitor className="size-3.5" /> <span className="hidden md:inline">Meet Mode</span>
          </button>
          <button
            onClick={() => setCallMode("phone")}
            className={cn(
              "flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md transition-all",
              callMode === "phone" ? "bg-white dark:bg-card text-primary shadow-sm ring-1 ring-border-strong" : "text-text-tertiary hover:text-text"
            )}
          >
            <Phone className="size-3.5" /> <span className="hidden md:inline">Phone Mode</span>
          </button>
        </div>

        {/* Center Controls (Speaker Toggle) */}
        <div className="flex-1 flex justify-center">
          {callMode === "phone" && (
            <div className="flex items-center bg-card-muted rounded-xl p-1 border border-line shadow-sm">
              <button
                onClick={() => setCurrentSpeaker("rep")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg transition-all",
                  currentSpeaker === "rep" ? "bg-primary text-bg shadow" : "text-text-tertiary hover:text-text hover:bg-scrim"
                )}
              >
                <Headset className="size-3.5" /> My Turn
              </button>
              <button
                onClick={() => setCurrentSpeaker("customer")}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg transition-all",
                  currentSpeaker === "customer" ? "bg-purple text-bg shadow" : "text-text-tertiary hover:text-text hover:bg-scrim"
                )}
              >
                <User className="size-3.5" /> Client's Turn
              </button>
            </div>
          )}
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 mr-2">
            <span
              className={cn(
                "size-2 rounded-full",
                isListening ? "bg-danger animate-pulse" : "bg-text-tertiary"
              )}
            />
            <span className="tnum text-sm font-semibold text-text-secondary">{clock(elapsed)}</span>
          </div>

          <button
            onClick={handleMicClick}
            className={cn(
              "flex items-center gap-2 rounded-xl px-4 py-1.5 text-sm font-semibold transition-all shadow-sm",
              isListening
                ? "bg-danger-soft text-danger hover:bg-danger/20"
                : "bg-primary text-bg hover:bg-primary/90 hover:shadow-md"
            )}
          >
            {isListening ? <MicOff className="size-4" /> : <Mic className="size-4" />}
            <span className="hidden sm:inline">{isListening ? "Mute" : "Listen"}</span>
          </button>

          <ThemeToggle />

          <button 
            onClick={handleLogout}
            title="Logout"
            className="grid size-8 place-items-center rounded-xl border border-line text-text-tertiary transition-colors hover:bg-danger-soft hover:text-danger hover:border-danger/30"
          >
            <LogOut className="size-3.5" />
          </button>
        </div>
      </header>
    </div>
  );
}
